import { umToCm } from "./custom-imposition.coordinates";
import { findAttachmentOutput, parseMaterialSizeLabel, resolveSheetFromAttachment } from "./custom-imposition.utils";
import type { PrintSheetMode } from "./types";

export const PRINT_SHEET_TOLERANCE_UM = 10;

export interface SheetDimensionsUm {
  widthUm: number;
  lengthUm: number;
}

export function almostEqualSheet(
  left: number,
  right: number,
  tolerance = PRINT_SHEET_TOLERANCE_UM,
): boolean {
  return Math.abs(left - right) <= tolerance;
}

export function isFullMaterial(
  purchased: SheetDimensionsUm | null | undefined,
  print: SheetDimensionsUm | null | undefined,
  tolerance = PRINT_SHEET_TOLERANCE_UM,
): boolean {
  if (!purchased?.widthUm || !purchased?.lengthUm || !print?.widthUm || !print?.lengthUm) {
    return true;
  }
  return (
    almostEqualSheet(purchased.widthUm, print.widthUm, tolerance) &&
    almostEqualSheet(purchased.lengthUm, print.lengthUm, tolerance)
  );
}

export function resolvePrintSheetMode(
  purchased: SheetDimensionsUm | null | undefined,
  print: SheetDimensionsUm | null | undefined,
): PrintSheetMode {
  return isFullMaterial(purchased, print) ? "full" : "custom";
}

export function isPrePrintActive(
  purchased: SheetDimensionsUm | null | undefined,
  print: SheetDimensionsUm | null | undefined,
): boolean {
  return resolvePrintSheetMode(purchased, print) === "custom";
}

export function printWorkingRequestCm(
  mode: PrintSheetMode | null | undefined,
  printWidthUm: number,
  printLengthUm: number,
): { PrintWorkingWidth: number; PrintWorkingLength: number } {
  if (mode !== "custom" || printWidthUm <= 0 || printLengthUm <= 0) {
    return { PrintWorkingWidth: 0, PrintWorkingLength: 0 };
  }
  return {
    PrintWorkingWidth: umToCm(printWidthUm),
    PrintWorkingLength: umToCm(printLengthUm),
  };
}

export function isPrintSheetObviouslyLargerThanRaw(
  print: SheetDimensionsUm | null | undefined,
  purchased: SheetDimensionsUm | null | undefined,
  tolerance = PRINT_SHEET_TOLERANCE_UM,
): boolean {
  if (!print?.widthUm || !print?.lengthUm || !purchased?.widthUm || !purchased?.lengthUm) {
    return false;
  }
  const fitsNative =
    print.widthUm <= purchased.widthUm + tolerance &&
    print.lengthUm <= purchased.lengthUm + tolerance;
  const fitsRotated =
    print.widthUm <= purchased.lengthUm + tolerance &&
    print.lengthUm <= purchased.widthUm + tolerance;
  return !fitsNative && !fitsRotated;
}

export function printSheetDraftKey(
  materialId: string | null | undefined,
  printWidthUm: number,
  printLengthUm: number,
): string {
  return `${materialId ?? ""}\u001f${Math.round(printWidthUm)}\u001f${Math.round(printLengthUm)}`;
}

export function resolvePurchasedSheetUm(input: {
  materialLabel?: string;
  purchasedWidthUm?: number;
  purchasedLengthUm?: number;
}): SheetDimensionsUm | null {
  if (input.purchasedWidthUm && input.purchasedLengthUm) {
    return { widthUm: input.purchasedWidthUm, lengthUm: input.purchasedLengthUm };
  }
  return parseMaterialSizeLabel(input.materialLabel);
}

export function resolveAutomaticPrintSheet(input: {
  outputs?: Array<{
    name?: string;
    orginalName?: string;
    materialWidth?: number;
    materialLength?: number;
  }>;
  materialLabel?: string;
  purchasedWidthUm?: number;
  purchasedLengthUm?: number;
}): {
  mode: PrintSheetMode;
  purchased: SheetDimensionsUm | null;
  print: SheetDimensionsUm | null;
} {
  const purchased = resolvePurchasedSheetUm(input);
  const attachment = findAttachmentOutput(input.outputs);
  const print = resolveSheetFromAttachment(attachment) ?? purchased;
  return {
    mode: resolvePrintSheetMode(purchased, print),
    purchased,
    print,
  };
}

export function canSeedAutomaticPrintSheet(input: {
  currentMaterialSizeId?: string | null;
  outputs?: Array<{
    name?: string;
    orginalName?: string;
    materialWidth?: number;
    materialLength?: number;
    rectangles?: unknown[];
  }>;
  materialLabel?: string;
  purchasedWidthUm?: number;
  purchasedLengthUm?: number;
}): boolean {
  if (!input.currentMaterialSizeId) {
    return false;
  }
  const attachment = findAttachmentOutput(input.outputs);
  if (!attachment?.rectangles?.length) {
    return false;
  }
  const resolved = resolveAutomaticPrintSheet(input);
  if (!resolved.print) {
    return false;
  }
  return !isPrintSheetObviouslyLargerThanRaw(resolved.print, resolved.purchased);
}

export function resolveCanvasSheetUm(
  mode: PrintSheetMode | null | undefined,
  purchased: SheetDimensionsUm | null | undefined,
  print: SheetDimensionsUm | null | undefined,
): SheetDimensionsUm {
  if (mode === "custom" && print?.widthUm && print?.lengthUm) {
    return print;
  }
  if (purchased?.widthUm && purchased?.lengthUm) {
    return purchased;
  }
  return print ?? { widthUm: 0, lengthUm: 0 };
}
